import React from 'react';
import { FiUser, FiPhone, FiMail, FiMapPin } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGridThree,
  FormGroup,
  Label,
  Input,
  Select,
  HelpText,
  InfoBox,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel
} from './FormStyles';
import { PersonalInfo, ContactInfo } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from './DocumentUpload';
import { PERSONAL_INFO_DOCUMENTS } from '@/config/documentsBySection';

interface PersonalInfoSectionProps {
  data: Partial<PersonalInfo & ContactInfo>;
  onChange: (data: Partial<PersonalInfo & ContactInfo>) => void;
  uploadedDocuments?: UploadedDocument[];
  onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
  readonly?: boolean;
}

export const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  data,
  onChange,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <>
      <FormSection>
        <SectionTitle>
          <FiUser />
          Informations Personnelles
        </SectionTitle>

        <InfoBox $type="info">
          <p>
            Remplissez vos informations personnelles. Les champs marqués d'un astérisque (*) sont obligatoires.
          </p>
        </InfoBox>

        <SubSectionTitle>Identité</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label $required>Civilité</Label>
            <Select
              value={data.civilite || ''}
              onChange={e => handleChange('civilite', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="M">M.</option>
              <option value="Mme">Mme</option>
              <option value="Mlle">Mlle</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $required>Nom de famille</Label>
            <Input
              type="text"
              value={data.nom || ''}
              onChange={e => handleChange('nom', e.target.value)}
              placeholder="Votre nom"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label $required>Prénom(s)</Label>
          <Input
            type="text"
            value={data.prenoms || ''}
            onChange={e => handleChange('prenoms', e.target.value)}
            placeholder="Vos prénoms"
            disabled={readonly}
          />
        </FormGroup>
        <FormGroup>
          <Label $required>Email</Label>
          <Input
            type="text"
            value={data.email || ''}
            onChange={e => handleChange('email', e.target.value)}
            placeholder="Email"
            disabled={readonly}
          />
        </FormGroup>

        <FormGrid>
          <FormGroup>
            <Label $required>Date de naissance</Label>
            <Input
              type="date"
              value={data.date_naissance || ''}
              onChange={e => handleChange('date_naissance', e.target.value)}
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Lieu de naissance</Label>
            <Input
              type="text"
              value={data.lieu_naissance || ''}
              onChange={e => handleChange('lieu_naissance', e.target.value)}
              placeholder="Ville de naissance"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label $required>Nationalité</Label>
            <Select
              value={data.nationalite || 'Sénégalaise'}
              onChange={e => handleChange('nationalite', e.target.value)}
              disabled={readonly}
            >
              <option value="Sénégalaise">Sénégalaise</option>
              <option value="Ivoirienne">Ivoirienne</option>
              <option value="Autre">Autre</option>
            </Select>
          </FormGroup>

          <FormGroup>
            <Label $required>N° CNI/Passeport</Label>
            <Input
              type="text"
              value={data.numero_cni_passeport || ''}
              onChange={e => handleChange('numero_cni_passeport', e.target.value)}
              placeholder="Ex: CI20230001234"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        <SubSectionTitle>Situation Familiale</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label $required>Situation matrimoniale</Label>
            <Select
              value={data.situation_matrimoniale || ''}
              onChange={e => handleChange('situation_matrimoniale', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Célibataire">Célibataire</option>
              <option value="Marié(e) coutumièrement">Marié(e) coutumièrement</option>
              <option value="Marié(e) civilement">Marié(e) civilement</option>
              <option value="Union libre">Union libre</option>
              <option value="Divorcé(e)">Divorcé(e)</option>
              <option value="Veuf(ve)">Veuf(ve)</option>
              <option value="Polygame">Polygame</option>
            </Select>
          </FormGroup>

          {data.situation_matrimoniale === 'Polygame' && (
            <FormGroup>
              <Label>Nombre d'épouses</Label>
              <Input
                type="number"
                min="1"
                value={data.nombre_epouses || ''}
                onChange={e => handleChange('nombre_epouses', parseInt(e.target.value))}
                disabled={readonly}
              />
            </FormGroup>
          )}
        </FormGrid>

        <FormGridThree>
          <FormGroup>
            <Label>Nombre d'enfants</Label>
            <Input
              type="number"
              min="0"
              value={data.nombre_enfants || 0}
              onChange={e => handleChange('nombre_enfants', parseInt(e.target.value))}
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Autres personnes à charge</Label>
            <Input
              type="number"
              min="0"
              value={data.autres_personnes_charge || 0}
              onChange={e => handleChange('autres_personnes_charge', parseInt(e.target.value))}
              disabled={readonly}
            />
          </FormGroup>
        </FormGridThree>
      </FormSection>

      <FormSection>
        <SectionTitle>
          <FiMapPin />
          Coordonnées
        </SectionTitle>

        <SubSectionTitle>Adresse Actuelle</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label $required>Quartier actuel</Label>
            <Input
              type="text"
              value={data.quartier_actuel || ''}
              onChange={e => handleChange('quartier_actuel', e.target.value)}
              placeholder="Nom du quartier"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label $required>Commune</Label>
            <Select
              value={data.commune || ''}
              onChange={e => handleChange('commune', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>

              <optgroup label="🏙️ Région de Dakar">
                <option value="Dakar">Dakar (capitale)</option>
                <option value="Pikine">Pikine</option>
                <option value="Guédiawaye">Guédiawaye</option>
                <option value="Rufisque">Rufisque</option>
                <option value="Keur Massar">Keur Massar</option>
              </optgroup>

              <optgroup label="🌴 Région de Thiès">
                <option value="Thiès">Thiès</option>
                <option value="Mbour">Mbour</option>
                <option value="Tivaouane">Tivaouane</option>
                <option value="Joal-Fadiouth">Joal-Fadiouth</option>
                <option value="Khombole">Khombole</option>
                <option value="Pout">Pout</option>
              </optgroup>

              <optgroup label="🌾 Région de Saint-Louis">
                <option value="Saint-Louis">Saint-Louis</option>
                <option value="Richard-Toll">Richard-Toll</option>
                <option value="Dagana">Dagana</option>
                <option value="Podor">Podor</option>
              </optgroup>

              <optgroup label="🐟 Région de Ziguinchor">
                <option value="Ziguinchor">Ziguinchor</option>
                <option value="Bignona">Bignona</option>
                <option value="Oussouye">Oussouye</option>
              </optgroup>

              <optgroup label="🐪 Région de Louga">
                <option value="Louga">Louga</option>
                <option value="Kébémer">Kébémer</option>
                <option value="Linguère">Linguère</option>
              </optgroup>

              <optgroup label="🌻 Région de Kaolack">
                <option value="Kaolack">Kaolack</option>
                <option value="Nioro du Rip">Nioro du Rip</option>
                <option value="Guinguinéo">Guinguinéo</option>
              </optgroup>

              <optgroup label="🌽 Région de Fatick">
                <option value="Fatick">Fatick</option>
                <option value="Foundiougne">Foundiougne</option>
                <option value="Gossas">Gossas</option>
              </optgroup>

              <optgroup label="🌍 Région de Diourbel">
                <option value="Diourbel">Diourbel</option>
                <option value="Bambey">Bambey</option>
                <option value="Mbacké">Mbacké (proche de Touba)</option>
                <option value="Touba">Touba</option>
              </optgroup>

              <optgroup label="🐄 Région de Kaffrine">
                <option value="Kaffrine">Kaffrine</option>
                <option value="Koungheul">Koungheul</option>
                <option value="Malem Hoddar">Malem Hoddar</option>
                <option value="Birkelane">Birkelane</option>
              </optgroup>

              <optgroup label="🌳 Région de Tambacounda">
                <option value="Tambacounda">Tambacounda</option>
                <option value="Goudiry">Goudiry</option>
                <option value="Koumpentoum">Koumpentoum</option>
                <option value="Bakel">Bakel</option>
              </optgroup>

              <optgroup label="🌾 Région de Kolda">
                <option value="Kolda">Kolda</option>
                <option value="Vélingara">Vélingara</option>
                <option value="Médina Yoro Foulah">Médina Yoro Foulah</option>
              </optgroup>

              <optgroup label="🌿 Région de Sédhiou">
                <option value="Sédhiou">Sédhiou</option>
                <option value="Bounkiling">Bounkiling</option>
                <option value="Goudomp">Goudomp</option>
              </optgroup>

              <optgroup label="🏞️ Région de Matam">
                <option value="Matam">Matam</option>
                <option value="Kanel">Kanel</option>
                <option value="Ranérou Ferlo">Ranérou Ferlo</option>
              </optgroup>
            </Select>
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label>Point de repère</Label>
          <Input
            type="text"
            value={data.point_repere || ''}
            onChange={e => handleChange('point_repere', e.target.value)}
            placeholder="Ex: Derrière la mosquée, face à la pharmacie..."
            disabled={readonly}
          />
          <HelpText>Indiquez un point de repère pour faciliter la localisation</HelpText>
        </FormGroup>

        <FormGrid>
          <FormGroup>
            <Label $required>Situation logement actuelle</Label>
            <Select
              value={data.situation_logement || ''}
              onChange={e => handleChange('situation_logement', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Locataire">Locataire</option>
              <option value="Hébergé famille">Hébergé par la famille</option>
              <option value="Propriétaire">Propriétaire</option>
              <option value="Logement précaire">Logement précaire</option>
              <option value="Logement fonction">Logement de fonction</option>
              <option value="Cour commune">Cour commune</option>
            </Select>
          </FormGroup>
        </FormGrid>

        <SubSectionTitle>Contact</SubSectionTitle>

        <FormGrid>
          <FormGroup>
            <Label $required>Téléphone principal</Label>
            <Input
              type="tel"
              value={data.telephone_1 || ''}
              onChange={e => handleChange('telephone_1', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Opérateur</Label>
            <Select
              value={data.operateur || ''}
              onChange={e => handleChange('operateur', e.target.value)}
              disabled={readonly}
            >
              <option value="">Sélectionnez...</option>
              <option value="Orange">Orange</option>
              <option value="MTN">MTN</option>
              <option value="Moov">Moov</option>
              <option value="Autre">Autre</option>
            </Select>
          </FormGroup>
        </FormGrid>

        <FormGrid>
          <FormGroup>
            <Label>Téléphone secondaire</Label>
            <Input
              type="tel"
              value={data.telephone_2 || ''}
              onChange={e => handleChange('telephone_2', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Email</Label>
            <Input
              type="email"
              value={data.email || ''}
              onChange={e => handleChange('email', e.target.value)}
              placeholder="votre.email@exemple.com"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label>WhatsApp</Label>
          <ToggleSwitch>
            <ToggleInput
              checked={data.whatsapp || false}
              onChange={e => handleChange('whatsapp', e.target.checked)}
              disabled={readonly}
            />
            <ToggleLabel>J'utilise WhatsApp</ToggleLabel>
          </ToggleSwitch>
        </FormGroup>

        {data.whatsapp && (
          <FormGroup>
            <Label>Numéro WhatsApp</Label>
            <Input
              type="tel"
              value={data.numero_whatsapp || data.telephone_1 || ''}
              onChange={e => handleChange('numero_whatsapp', e.target.value)}
              placeholder="+221 X XX XX XX XX"
              disabled={readonly}
            />
            <HelpText>Par défaut, votre téléphone principal</HelpText>
          </FormGroup>
        )}

        {/* Documents justificatifs pour cette section */}
        {onDocumentUpload && onDocumentRemove && (
          <DocumentUpload
            sectionTitle="Informations Personnelles"
            documents={PERSONAL_INFO_DOCUMENTS}
            uploadedFiles={uploadedDocuments}
            onFileSelect={onDocumentUpload}
            onFileRemove={onDocumentRemove}
            readonly={readonly}
          />
        )}
      </FormSection>
    </>
  );
};
